import React, { FC, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Modal } from 'shared/ui/Modal';
import { Table } from 'shared/ui/Table';
import { getClientDateWithTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useGetCurrencySymbol } from 'app/appState';
import { PriceHistoryDocument, useGetUnitPriceHistoryQuery } from 'entities/PriceHistory';
import { getUserName } from 'entities/User';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { TabSwitcher } from 'shared/ui/TabSwitcher';

interface UnitPriceHistoryModalProps {
  isOpened: boolean;
  boxId?: string;
  cachedBoxId?: string;
  insuranceId?: string;
  cachedInsuranceId?: string;
  zIndex?: number;
  onClose: () => void;
}

const getDiffValueClassName = (diffValue: number): string => {
  return `text-sm font-semibold ${diffValue > 0 ? 'text-green-600' : 'text-red-600'}`;
};

const renderAmountWithDiff = (amount: number, diffAmount: number, currencySymbol: string): JSX.Element => (
  <div className="text-primary font-normal">
    <div>
      {roundNumber(amount)} {currencySymbol}
    </div>
    {diffAmount !== 0 && (
      <div className={getDiffValueClassName(diffAmount)}>
        {diffAmount > 0 ? '+' : ''}
        {roundNumber(diffAmount)} {currencySymbol}
      </div>
    )}
  </div>
);

export const UnitPriceHistoryModal: FC<UnitPriceHistoryModalProps> = memo((props) => {
  const { isOpened, boxId, cachedBoxId, cachedInsuranceId, insuranceId, zIndex, onClose } = props;

  const { t } = useAppTranslation('contracts');
  const currencySymbol = useGetCurrencySymbol();
  const [selectedTabKey, setSelectedTabKey] = useState<InvoiceFrequencyType>(InvoiceFrequencyType.MONTH);

  const skip = !isOpened || (!boxId && !cachedBoxId && !insuranceId && !cachedInsuranceId);

  const { data: priceHistory, isLoading } = useGetUnitPriceHistoryQuery(
    { boxId, insuranceId, cachedBoxId, cachedInsuranceId, rentType: selectedTabKey },
    { skip },
  );

  const switchTab = useCallback((tabKey: string): void => {
    setSelectedTabKey(tabKey as InvoiceFrequencyType);
  }, []);

  const tabs = useMemo(() => {
    const commonTabs = [
      { key: 'month', label: t('Month') },
      { key: 'week', label: t('Week') },
      { key: 'day', label: t('Day') },
    ];

    return commonTabs;
  }, [t]);

  const columns = useMemo<ColumnsType<PriceHistoryDocument>>(
    () => [
      {
        title: t('Date'),
        key: 'createdAt',
        width: '25%',
        render: (_, record) => <div className="text-primary font-normal">{getClientDateWithTimeFormat(record.createdAt)}</div>,
      },
      {
        title: t('Unit'),
        key: 'target',
        width: '15%',
        render: (_, record) => t(record.target),
      },
      {
        title: t('Rent type'),
        key: 'rentType',
        width: '15%',
        render: (_, record) => t(record.rentType),
      },
      {
        title: t('Current {{rentType}} rate', {
          rentType: t(selectedTabKey),
        }),
        key: 'depositAmount',
        width: '25%',
        render: (_, record) => renderAmountWithDiff(record.updatedPrice, record.difference, currencySymbol),
      },
      {
        title: t('Updated by'),
        key: 'updatedBy',
        render: (_, record) => <div className="text-primary font-normal">{getUserName(record?.updatedBy)}</div>,
      },
    ],
    [t, selectedTabKey, currencySymbol],
  );

  const fistUpdatedPrice = priceHistory?.at(-1);

  return (
    <Modal width={1200} isOpen={isOpened} onClose={onClose} zIndex={zIndex}>
      <div className="font-semibold text-2xl">{t('Price history')}</div>
      <TabSwitcher tabs={tabs} selectedTabKey={selectedTabKey} onChangeTab={switchTab}>
        <div className="mb-10">
          {fistUpdatedPrice && (
            <div className="flex gap-2">
              <span className="text-lg mb-2">
                {t('Initial {{rentType}} rate', {
                  rentType: t(selectedTabKey),
                })}
                :
              </span>
              <span className="text-lg mb-2">
                {fistUpdatedPrice.updatedPrice - fistUpdatedPrice.difference} {currencySymbol}
              </span>
            </div>
          )}
          <Table
            columns={columns}
            data={priceHistory || []}
            loading={isLoading}
            rowKey="priceHistoryId"
            search={false}
            pagination={{
              pageSize: 10,
            }}
          />
        </div>
      </TabSwitcher>
    </Modal>
  );
});
